import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Download, IndianRupee, Users, Loader2, AlertCircle, BarChart3, UserCheck,
  CreditCard, TrendingUp, Clock, RefreshCw, Search, AlertTriangle,
  ChevronLeft, ChevronRight, Sparkles, TrendingDown, Heart, UserPlus, FileSpreadsheet, X, FileText,
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { toast } from '../hooks/use-toast';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ---------- Helpers ----------
import { toLocalYMD } from '../utils/timeSlots';

const fmtINR = (n) => `\u20B9${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
// LOCAL-time date formatter — see note in `utils/timeSlots.js#toLocalYMD`.
// Using `d.toISOString().split('T')[0]` here rolled range boundaries back
// by one day for admins in IST between 00:00 and 05:30 IST — the exact
// "This Month starts on the 31st of previous month" bug.
const toDay = (d) => toLocalYMD(d);
const today = () => toDay(new Date());
const daysAgo = (n) => toDay(new Date(Date.now() - n * 86400_000));
const firstOfMonth = (offset = 0) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset);
  return toDay(d);
};
const lastOfMonth = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() + offset + 1, 0);
  return toDay(d);
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SPORTS = ['football', 'padel', 'pickleball', 'cricket', 'other'];

// ---------- Direct CSV download with auth ----------
const downloadCsv = async (path, filename) => {
  const token = localStorage.getItem('token');
  const url = `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Server returned ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast({ title: 'Export started', description: filename });
  } catch (e) {
    toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
  }
};

// ---------- KPI card ----------
const Kpi = ({ icon: Icon, label, value, sub, accent = 'text-gray-900', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-400" />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className={`text-xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

// ---------- Filters component ----------
const FiltersBar = ({ filters, onChange, onApply, loading, isSuperadmin, facilities, facilityName }) => {
  const setField = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <Card>
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
          <div>
            <label className="text-[11px] font-medium text-gray-600">From</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setField('date_from', e.target.value)} className="h-9 text-sm" data-testid="reports-date-from" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-600">To</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setField('date_to', e.target.value)} className="h-9 text-sm" data-testid="reports-date-to" />
          </div>
          {isSuperadmin ? (
            <div>
              <label className="text-[11px] font-medium text-gray-600">Venue</label>
              <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={filters.facility_id || ''} onChange={(e) => setField('facility_id', e.target.value || null)} data-testid="reports-facility-filter">
                <option value="">All Venues</option>
                {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-[11px] font-medium text-gray-600">Venue</label>
              <div className="h-9 px-2 flex items-center text-sm text-gray-700 truncate">{facilityName || '—'}</div>
            </div>
          )}
          <div>
            <label className="text-[11px] font-medium text-gray-600">Sport</label>
            <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={filters.sport || ''} onChange={(e) => setField('sport', e.target.value || null)} data-testid="reports-sport-filter">
              <option value="">All Sports</option>
              {SPORTS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={onApply} disabled={loading} className="w-full h-9" data-testid="reports-apply">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Today', f: () => ({ date_from: today(), date_to: today() }) },
            { label: '7 Days', f: () => ({ date_from: daysAgo(6), date_to: today() }) },
            { label: '30 Days', f: () => ({ date_from: daysAgo(29), date_to: today() }) },
            { label: 'This Month', f: () => ({ date_from: firstOfMonth(0), date_to: today() }) },
            { label: 'Last Month', f: () => ({ date_from: firstOfMonth(-1), date_to: lastOfMonth(-1) }) },
          ].map((p) => (
            <Button key={p.label} variant="outline" size="sm" onClick={() => onChange({ ...filters, ...p.f() })} data-testid={`reports-preset-${p.label.replace(/\s/g,'').toLowerCase()}`}>
              {p.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Revenue trend chart ----------
const VERTICAL_COLORS = {
  bookings: '#2563eb',          // blue
  rentals: '#0ea5e9',           // sky
  events: '#9333ea',            // purple
  community_games: '#16a34a',   // green
  coaching: '#f59e0b',          // amber
};

// Colour swatches for booking SOURCES (used in Source Trend chart).
const SOURCE_COLORS = {
  admin:  '#475569',  // slate
  direct: '#2563eb',  // blue
  web:    '#06b6d4',  // cyan
  playo:  '#f97316',  // orange
  hudle:  '#16a34a',  // green
};
const SOURCE_KEYS = ['admin', 'direct', 'web', 'playo', 'hudle'];

const RevenueTrendChart = ({ daily }) => {
  const [grain, setGrain] = useState('daily');
  const [mode, setMode] = useState('bookings'); // 'bookings' | 'all'

  const data = useMemo(() => {
    if (!daily?.length) return [];
    const aggregate = (arr) => arr.reduce((acc, d) => ({
      bookings_amount: (acc.bookings_amount || 0) + d.bookings_amount,
      bookings_collected: (acc.bookings_collected || 0) + d.bookings_collected,
      events_amount: (acc.events_amount || 0) + d.events_amount,
      community_games_amount: (acc.community_games_amount || 0) + d.community_games_amount,
      coaching_amount: (acc.coaching_amount || 0) + d.coaching_amount,
      total_amount: (acc.total_amount || 0) + d.total_amount,
    }), {});
    if (grain === 'daily') return daily;
    const bucket = {};
    daily.forEach((d) => {
      const dt = new Date(d.date);
      let key;
      if (grain === 'weekly') {
        const wk = new Date(dt); wk.setDate(dt.getDate() - dt.getDay());
        key = toDay(wk);
      } else {
        key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      }
      bucket[key] = bucket[key] || { date: key, rows: [] };
      bucket[key].rows.push(d);
    });
    return Object.values(bucket).map((b) => ({ date: b.date, ...aggregate(b.rows) }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [daily, grain]);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row justify-between items-center flex-wrap gap-2">
        <CardTitle className="text-sm">Revenue Trend</CardTitle>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1">
            <Button size="sm" variant={mode === 'bookings' ? 'default' : 'outline'} onClick={() => setMode('bookings')} className="h-7 text-[11px] px-2" data-testid="trend-mode-bookings">Bookings Only</Button>
            <Button size="sm" variant={mode === 'all' ? 'default' : 'outline'} onClick={() => setMode('all')} className="h-7 text-[11px] px-2" data-testid="trend-mode-all">All Revenue</Button>
          </div>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex gap-1">
            {['daily', 'weekly', 'monthly'].map((g) => (
              <Button key={g} size="sm" variant={grain === g ? 'default' : 'outline'} onClick={() => setGrain(g)} className="h-7 text-[11px] px-2" data-testid={`trend-grain-${g}`}>
                {g[0].toUpperCase() + g.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            {mode === 'bookings' ? (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v / 1000 >= 1 ? `${(v/1000).toFixed(1)}k` : v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="bookings_amount" name="Revenue" stroke={VERTICAL_COLORS.bookings} strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="bookings_collected" name="Collected" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            ) : (
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v / 1000 >= 1 ? `${(v/1000).toFixed(1)}k` : v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="bookings_amount" name="Bookings" stackId="1" stroke={VERTICAL_COLORS.bookings} fill={VERTICAL_COLORS.bookings} fillOpacity={0.6} />
                <Area type="monotone" dataKey="events_amount" name="Events" stackId="1" stroke={VERTICAL_COLORS.events} fill={VERTICAL_COLORS.events} fillOpacity={0.6} />
                <Area type="monotone" dataKey="community_games_amount" name="Community Games" stackId="1" stroke={VERTICAL_COLORS.community_games} fill={VERTICAL_COLORS.community_games} fillOpacity={0.6} />
                <Area type="monotone" dataKey="coaching_amount" name="Coaching" stackId="1" stroke={VERTICAL_COLORS.coaching} fill={VERTICAL_COLORS.coaching} fillOpacity={0.6} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Source Trend chart (per-source revenue over time) ----------
const SourceTrendChart = ({ trends }) => {
  const [grain, setGrain] = useState('monthly');
  const [chartKind, setChartKind] = useState('stacked'); // 'stacked' | 'lines'

  const data = useMemo(() => {
    if (!trends?.length) return [];
    const aggregate = (rows) => SOURCE_KEYS.reduce((acc, k) => {
      acc[k] = rows.reduce((s, r) => s + (r[k] || 0), 0);
      return acc;
    }, {});
    if (grain === 'daily') return trends.map((r) => ({ ...r }));
    const bucket = {};
    trends.forEach((r) => {
      const dt = new Date(r.date);
      let key;
      if (grain === 'weekly') {
        const wk = new Date(dt); wk.setDate(dt.getDate() - dt.getDay());
        key = toDay(wk);
      } else {
        key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      }
      bucket[key] = bucket[key] || { date: key, rows: [] };
      bucket[key].rows.push(r);
    });
    return Object.values(bucket)
      .map((b) => ({ date: b.date, ...aggregate(b.rows) }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [trends, grain]);

  // Hide channels that contributed zero over the entire range so the
  // legend stays clean (e.g. a venue that doesn't use Playo at all).
  const activeKeys = useMemo(() => {
    const totals = {};
    SOURCE_KEYS.forEach((k) => { totals[k] = data.reduce((s, r) => s + (r[k] || 0), 0); });
    const active = SOURCE_KEYS.filter((k) => totals[k] > 0);
    return active.length ? active : SOURCE_KEYS;
  }, [data]);

  return (
    <Card data-testid="source-trend-card">
      <CardHeader className="pb-2 flex flex-row justify-between items-center flex-wrap gap-2">
        <CardTitle className="text-sm">Source Trends</CardTitle>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1">
            <Button size="sm" variant={chartKind === 'stacked' ? 'default' : 'outline'} onClick={() => setChartKind('stacked')} className="h-7 text-[11px] px-2" data-testid="source-trend-kind-stacked">Stacked</Button>
            <Button size="sm" variant={chartKind === 'lines' ? 'default' : 'outline'} onClick={() => setChartKind('lines')} className="h-7 text-[11px] px-2" data-testid="source-trend-kind-lines">Lines</Button>
          </div>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex gap-1">
            {['daily', 'weekly', 'monthly'].map((g) => (
              <Button key={g} size="sm" variant={grain === g ? 'default' : 'outline'} onClick={() => setGrain(g)} className="h-7 text-[11px] px-2" data-testid={`source-trend-grain-${g}`}>
                {g[0].toUpperCase() + g.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            {chartKind === 'stacked' ? (
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v / 1000 >= 1 ? `${(v/1000).toFixed(1)}k` : v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {activeKeys.map((k) => (
                  <Area key={k} type="monotone" dataKey={k} name={k[0].toUpperCase() + k.slice(1)} stackId="1" stroke={SOURCE_COLORS[k]} fill={SOURCE_COLORS[k]} fillOpacity={0.6} />
                ))}
              </AreaChart>
            ) : (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v / 1000 >= 1 ? `${(v/1000).toFixed(1)}k` : v}`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {activeKeys.map((k) => (
                  <Line key={k} type="monotone" dataKey={k} name={k[0].toUpperCase() + k.slice(1)} stroke={SOURCE_COLORS[k]} strokeWidth={2} dot={{ r: 2 }} />
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};


// ---------- Venue Revenue Summary card (Phase 2) ----------
const VENUE_LINKS = {
  bookings: '/admin/bookings',
  rentals: '/admin/bookings',
  events: '/admin/events',
  community_games: '/admin/community-games',
  coaching: '/admin/coaching',
};
const VENUE_LABELS = {
  bookings: 'Court Bookings',
  rentals: 'Rentals',
  events: 'Events',
  community_games: 'Community Games',
  coaching: 'Coaching',
};

const VenueRevenueCard = ({ vr }) => {
  if (!vr) return null;
  const keys = ['bookings', 'rentals', 'events', 'community_games', 'coaching'].filter((k) => vr[k]);
  const max = Math.max(...keys.map((k) => vr[k].amount), 1);
  return (
    <Card data-testid="venue-revenue-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">{vr.total_label || 'Ground Booking Value (Court + Rentals)'}</CardTitle></CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-gray-900 mb-1" data-testid="venue-revenue-total">{fmtINR(vr.total)}</div>
        <p className="text-[11px] text-gray-400 mb-3">Court + rentals only — the venue's settled revenue. Events &amp; community games are shown below but not added (their court cost is already in bookings).</p>
        <div className="space-y-2">
          {keys.map((k) => {
            const v = vr[k];
            const pct = (v.amount / max) * 100;
            const excluded = v.in_total === false;
            return (
              <a key={k} href={VENUE_LINKS[k]} className="block group" data-testid={`venue-revenue-${k}`}>
                <div className="flex items-baseline justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700 group-hover:text-blue-600">{VENUE_LABELS[k]} →</span>
                  <span className="font-semibold text-gray-900">{fmtINR(v.amount)} <span className="text-gray-400 ml-1">{excluded ? 'not in total' : `${v.percentage.toFixed(1)}%`}</span></span>
                </div>
                <div className={`h-2 rounded-full bg-gray-100 overflow-hidden ${excluded ? 'opacity-50' : ''}`}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${Math.max(pct, v.amount > 0 ? 2 : 0)}%`, backgroundColor: VERTICAL_COLORS[k] }}
                  />
                </div>
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Overview tab ----------
const OverviewTab = ({ summary, loading, error }) => {
  if (loading && !summary) return <SkeletonOverview />;
  if (error) return <ErrorBox msg={error} />;
  if (!summary) return null;

  const { revenue, monthly_billing: mb, by_source, by_sport, by_payment_status } = summary;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={IndianRupee} label="Court Revenue" value={fmtINR(revenue.total_amount)} testId="kpi-revenue" />
        <Kpi icon={CreditCard} label="Collected" value={fmtINR(revenue.total_collected)} sub={`Collection rate ${fmtPct(revenue.collection_rate)}`} accent="text-blue-700" testId="kpi-collected" />
        <Kpi icon={AlertCircle} label="Pending" value={fmtINR(revenue.total_pending)} accent={revenue.total_pending > 0 ? 'text-amber-600' : 'text-gray-900'} testId="kpi-pending" />
        <Kpi icon={Users} label="Unique Customers" value={summary.unique_customers} sub={`${summary.new_customers} new · ${summary.returning_customers} returning`} accent="text-purple-700" testId="kpi-customers" />
      </div>

      <VenueRevenueCard vr={summary.venue_revenue} />

      <RevenueTrendChart daily={summary.daily_revenue} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Bookings</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row label="Total" value={summary.total_bookings} />
            <Row label="Confirmed" value={summary.confirmed_bookings} cls="text-blue-700" />
            <Row label="Cancelled" value={summary.cancelled_bookings} cls="text-red-600" />
            <Row label="Cancellation Rate" value={fmtPct(summary.cancellation_rate)} cls="text-amber-600" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Payment Status</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {['paid', 'pending', 'partial', 'monthly'].map((k) => (
              <Row key={k} label={k[0].toUpperCase() + k.slice(1)} value={`${by_payment_status[k].count} · ${fmtINR(by_payment_status[k].amount)}`} />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Billing</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex items-baseline justify-between"><span className="text-gray-500">Customers</span><span className="font-semibold">{mb.customers_count}</span></div>
            <div className="flex items-baseline justify-between"><span className="text-gray-500">Outstanding</span><span className="font-bold text-amber-700">{fmtINR(mb.total_outstanding)}</span></div>
            <div className="flex items-baseline justify-between"><span className="text-gray-500">Bookings</span><span className="font-semibold">{mb.booking_count}</span></div>
            <a href="/admin/bookings" className="block text-xs text-blue-600 hover:underline mt-1" data-testid="monthly-billing-link">Go to Monthly Payments →</a>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Source</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-xs text-gray-500">
                <th className="text-left py-1.5">Source</th>
                <th className="text-right">Bookings</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Collected</th>
                <th className="text-right">Rate</th>
              </tr></thead>
              <tbody>
                {Object.entries(by_source).map(([src, v]) => {
                  const rate = v.revenue > 0 ? (v.collected / v.revenue * 100) : 0;
                  return (
                    <tr key={src} className="border-b last:border-0">
                      <td className="py-1.5 capitalize font-medium">{src}</td>
                      <td className="text-right">{v.bookings}</td>
                      <td className="text-right">{fmtINR(v.revenue)}</td>
                      <td className="text-right">{fmtINR(v.collected)}</td>
                      <td className="text-right text-gray-500">{fmtPct(rate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <SourceTrendChart trends={summary.source_trends} />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Revenue by Sport</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-xs text-gray-500">
                <th className="text-left py-1.5">Sport</th>
                <th className="text-right">Bookings</th>
                <th className="text-right">Revenue</th>
              </tr></thead>
              <tbody>
                {Object.entries(by_sport).map(([sp, v]) => (
                  <tr key={sp} className="border-b last:border-0">
                    <td className="py-1.5 capitalize font-medium">{sp}</td>
                    <td className="text-right">{v.bookings}</td>
                    <td className="text-right">{fmtINR(v.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: per-vertical summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <VerticalCard
          title="Events"
          primaryLabel="Registrations" primaryValue={summary.events.total_registrations}
          revenue={summary.events.total_revenue}
          metaLabel="Events" metaValue={summary.events.unique_events}
          href="/admin/events" color={VERTICAL_COLORS.events} testId="card-events"
        />
        <VerticalCard
          title="Community Games"
          primaryLabel="Participants" primaryValue={summary.community_games.total_participants}
          revenue={summary.community_games.total_revenue}
          metaLabel="Games" metaValue={summary.community_games.total_games}
          href="/admin/community-games" color={VERTICAL_COLORS.community_games} testId="card-cg"
        />
        <VerticalCard
          title="Coaching"
          primaryLabel="Registrations" primaryValue={summary.coaching.total_registrations}
          revenue={summary.coaching.total_revenue}
          metaLabel="Programmes" metaValue={summary.coaching.unique_programmes}
          href="/admin/coaching" color={VERTICAL_COLORS.coaching} testId="card-coaching"
        />
      </div>
    </>
  );
};

const VerticalCard = ({ title, primaryLabel, primaryValue, revenue, metaLabel, metaValue, href, color, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2 flex flex-row items-center justify-between">
      <CardTitle className="text-sm">{title}</CardTitle>
      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    </CardHeader>
    <CardContent className="space-y-1.5 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-gray-500">{primaryLabel}</span>
        <span className="font-semibold">{primaryValue}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-gray-500">Revenue</span>
        <span className="font-bold" style={{ color }}>{fmtINR(revenue)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-gray-500">{metaLabel}</span>
        <span className="font-semibold">{metaValue}</span>
      </div>
      <a href={href} className="block text-xs text-blue-600 hover:underline mt-1">View {title} →</a>
    </CardContent>
  </Card>
);

const Row = ({ label, value, cls = '' }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={`font-semibold ${cls}`}>{value}</span>
  </div>
);

const SkeletonOverview = () => (
  <div className="space-y-3 animate-pulse">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[1,2,3,4].map((i) => <div key={i} className="h-20 bg-gray-100 rounded" />)}
    </div>
    <div className="h-72 bg-gray-100 rounded" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {[1,2,3].map((i) => <div key={i} className="h-40 bg-gray-100 rounded" />)}
    </div>
  </div>
);

const ErrorBox = ({ msg }) => (
  <div className="rounded-lg border border-red-100 bg-red-50 p-4 flex items-start gap-2">
    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
    <div>
      <div className="text-sm font-semibold text-red-900">Could not load report data.</div>
      <div className="text-xs text-red-700 mt-0.5">{msg || 'Try again.'}</div>
    </div>
  </div>
);

// ---------- Customer Segments Panel (Phase 3 — Customer Intelligence) ----------
const SEGMENT_META = {
  high_value:      { label: 'High Value',      icon: Sparkles,    color: '#0A2C93', bg: 'bg-blue-50',   text: 'text-blue-800',   border: 'border-blue-200' },
  at_risk:         { label: 'At Risk',         icon: TrendingDown,color: '#DC2626', bg: 'bg-red-50',    text: 'text-red-800',    border: 'border-red-200' },
  loyal:           { label: 'Loyal',           icon: Heart,       color: '#9333EA', bg: 'bg-purple-50', text: 'text-purple-800', border: 'border-purple-200' },
  new:             { label: 'New',             icon: UserPlus,    color: '#16A34A', bg: 'bg-green-50',  text: 'text-green-800',  border: 'border-green-200' },
  monthly_debtors: { label: 'Monthly Debtors', icon: AlertTriangle,color:'#D97706', bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200' },
};

const SegmentsPanel = ({ segments, loading, activeSegment, onSelect }) => {
  if (loading && !segments) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 animate-pulse">
        {[1,2,3,4,5].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}
      </div>
    );
  }
  if (!segments) return null;
  const order = ['high_value','at_risk','loyal','new','monthly_debtors'];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="customer-segments-panel">
      {order.map((key) => {
        const meta = SEGMENT_META[key];
        const seg = segments.segments[key];
        const Icon = meta.icon;
        const isActive = activeSegment === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(isActive ? null : key)}
            className={`text-left rounded-lg border ${meta.border} ${meta.bg} p-3 transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 ${isActive ? 'ring-2 ring-offset-1' : ''}`}
            style={isActive ? { '--tw-ring-color': meta.color } : {}}
            data-testid={`segment-card-${key}`}
          >
            <div className="flex items-center justify-between mb-1">
              <Icon className={`h-4 w-4 ${meta.text}`} />
              {isActive && <X className={`h-3.5 w-3.5 ${meta.text}`} />}
            </div>
            <div className={`text-2xl font-bold ${meta.text}`}>{seg.count}</div>
            <div className={`text-[11px] font-medium ${meta.text} mt-0.5`}>{meta.label}</div>
            <div className="text-[10px] text-gray-500 mt-1 line-clamp-2">{seg.description}</div>
          </button>
        );
      })}
    </div>
  );
};

// ---------- Customer Profile Drawer ----------
const CustomerProfileDrawer = ({ open, phone, onOpenChange, facilityId, segmentKeys = [] }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('bookings');

  useEffect(() => {
    if (!open || !phone) return;
    setProfile(null); setError(''); setLoading(true); setTab('bookings');
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (facilityId) qs.set('facility_id', facilityId);
        const path = qs.toString() ? `/reports/customers/${encodeURIComponent(phone)}?${qs.toString()}` : `/reports/customers/${encodeURIComponent(phone)}`;
        const res = await api.request(path);
        setProfile(res);
      } catch (e) {
        setError(e.message || 'Failed to load profile');
      } finally { setLoading(false); }
    })();
  }, [open, phone, facilityId]);

  const exportXlsx = async () => {
    const token = localStorage.getItem('token');
    const qs = new URLSearchParams();
    if (facilityId) qs.set('facility_id', facilityId);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/customers/${encodeURIComponent(phone)}/export${qs.toString() ? `?${qs.toString()}` : ''}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `PlaySpaces_Customer_${phone}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast({ title: 'Excel export ready', description: a.download });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="customer-profile-drawer">
        <SheetHeader className="pb-2 border-b">
          <SheetTitle className="text-base">Customer Profile</SheetTitle>
        </SheetHeader>
        {loading && (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}
        {error && !loading && (
          <div className="mt-4 rounded border border-red-100 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}
        {profile && !loading && (
          <div className="mt-3 space-y-4">
            {/* Identity */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-bold truncate" data-testid="profile-name">{profile.name || 'Unknown'}</div>
                <div className="text-sm text-gray-600" data-testid="profile-phone">{profile.phone}</div>
                {profile.email && <div className="text-xs text-gray-500 truncate">{profile.email}</div>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {segmentKeys.includes('high_value') && <Badge cls="bg-blue-100 text-blue-800">HIGH VALUE</Badge>}
                  {profile.segments.at_risk && <Badge cls="bg-red-100 text-red-700">AT RISK</Badge>}
                  {profile.segments.loyal && <Badge cls="bg-purple-100 text-purple-700">LOYAL</Badge>}
                  {profile.segments.new && <Badge cls="bg-green-100 text-green-700">NEW</Badge>}
                  {profile.segments.monthly_debtor && <Badge cls="bg-amber-100 text-amber-700">MONTHLY DEBT</Badge>}
                  {profile.sports_played?.map((s) => <Badge key={s} cls="bg-gray-100 text-gray-700 capitalize">{s}</Badge>)}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="profile-export-xlsx">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel
              </Button>
            </div>

            {/* Lifetime stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi icon={IndianRupee} label="Lifetime Spend" value={fmtINR(profile.lifetime_stats.total_spend)} accent="text-blue-700" testId="profile-stat-spend" />
              <Kpi icon={BarChart3} label="Activities" value={profile.lifetime_stats.activity_count} sub={`${profile.lifetime_stats.bookings_count} bookings`} testId="profile-stat-activities" />
              <Kpi icon={Clock} label="Days Since Last" value={profile.lifetime_stats.days_since_last_activity ?? '—'} sub={profile.lifetime_stats.last_activity_date || '—'} testId="profile-stat-recency" />
              <Kpi icon={AlertCircle} label="Monthly Due" value={fmtINR(profile.lifetime_stats.monthly_debt_outstanding)} accent={profile.lifetime_stats.monthly_debt_outstanding > 0 ? 'text-amber-700' : 'text-gray-900'} testId="profile-stat-due" />
            </div>

            {/* Spend by Vertical */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Spend by Vertical</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {['bookings', 'events', 'coaching', 'community_games'].map((k) => {
                  const v = profile.spend_by_vertical[k] || 0;
                  const max = profile.spend_by_vertical.total || 1;
                  return (
                    <div key={k} data-testid={`profile-spend-${k}`}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="capitalize text-gray-700">{k.replace('_', ' ')}</span>
                        <span className="font-semibold">{fmtINR(v)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-1.5 rounded-full" style={{ width: `${(v/max)*100}%`, backgroundColor: VERTICAL_COLORS[k] }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Activity Tabs */}
            <div className="flex gap-1 border-b">
              {[
                { id: 'bookings', label: `Bookings (${profile.bookings.length})` },
                { id: 'events', label: `Events (${profile.events.length})` },
                { id: 'coaching', label: `Coaching (${profile.coaching.length})` },
                { id: 'community_games', label: `Games (${profile.community_games.length})` },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                  data-testid={`profile-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Activity Lists */}
            <div className="space-y-2">
              {tab === 'bookings' && (profile.bookings.length === 0 ? <Empty msg="No bookings yet." /> : profile.bookings.map((b) => (
                <div key={b.id} className="border rounded-lg p-2.5 text-sm" data-testid={`profile-booking-${b.id}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-medium">{b.date} · {b.start_time}–{b.end_time}</div>
                    <div className="font-bold text-gray-900">{fmtINR(b.amount)}</div>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-1.5 items-center">
                    <span className="capitalize">{b.sport}</span>
                    <span>·</span>
                    <span>{b.court_name || b.court_id || '—'}</span>
                    <span>·</span>
                    <Badge cls={b.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>{b.status}</Badge>
                    <Badge cls={b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{b.payment_status}</Badge>
                    <span className="text-gray-400">{b.source}</span>
                  </div>
                </div>
              )))}
              {tab === 'events' && (profile.events.length === 0 ? <Empty msg="No event registrations." /> : profile.events.map((e) => (
                <div key={e.id} className="border rounded-lg p-2.5 text-sm" data-testid={`profile-event-${e.id}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-medium truncate">{e.event_title} — {e.activity_title}</div>
                    <div className="font-bold">{fmtINR(e.amount_paid)}</div>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-1.5">
                    <span>{e.activity_date} · {e.activity_start_time}</span>
                    <span>·</span>
                    <span className="capitalize">{e.sport}</span>
                    <span>·</span>
                    <Badge cls="bg-purple-100 text-purple-700">{e.activity_type}</Badge>
                    <Badge cls="bg-green-100 text-green-700">{e.payment_status}</Badge>
                  </div>
                </div>
              )))}
              {tab === 'coaching' && (profile.coaching.length === 0 ? <Empty msg="No coaching registrations." /> : profile.coaching.map((c) => (
                <div key={c.id} className="border rounded-lg p-2.5 text-sm" data-testid={`profile-coaching-${c.id}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-medium truncate">{c.programme_title}</div>
                    <div className="font-bold">{fmtINR(c.amount_paid)}</div>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-1.5">
                    <span>{c.package_label} ({c.package_sessions} sessions)</span>
                    <span>·</span>
                    <span className="capitalize">{c.sport}</span>
                    {c.level && <><span>·</span><span className="capitalize">{c.level}</span></>}
                    <span>·</span>
                    <Badge cls="bg-green-100 text-green-700">{c.payment_status}</Badge>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">Registered {(c.registered_at || '').slice(0,10)}</div>
                </div>
              )))}
              {tab === 'community_games' && (profile.community_games.length === 0 ? <Empty msg="No community games played." /> : profile.community_games.map((g) => (
                <div key={g.id} className="border rounded-lg p-2.5 text-sm" data-testid={`profile-cg-${g.id}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-medium capitalize">{g.sport} · {g.format}</div>
                    <div className="font-bold">{fmtINR(g.amount_paid)}</div>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-1.5">
                    <span>{g.date} · {g.start_time}–{g.end_time}</span>
                    <span>·</span>
                    <span>{g.court_name}</span>
                    <span>·</span>
                    <Badge cls="bg-green-100 text-green-700">{g.payment_status}</Badge>
                  </div>
                </div>
              )))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Empty = ({ msg }) => <div className="py-6 text-center text-sm text-gray-500">{msg}</div>;

// ---------- Customers tab ----------
const CustomersTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('total_spend');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all'); // all | new | churn | outstanding
  const [segments, setSegments] = useState(null);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);
  const [drawerPhone, setDrawerPhone] = useState(null);

  // Phones-in-segment list for the active segment. Passed to backend as the
  // `phones=` filter so /reports/customers returns lifetime-scoped rows for
  // exactly those phones — guaranteeing the list count matches the segment
  // card count (no longer reliant on a client-side intersect of one page).
  const segmentPhones = useMemo(() => {
    if (!segments || !activeSegment) return null;
    return segments.segment_phones?.[activeSegment] || [];
  }, [segments, activeSegment]);

  // Reset to page 1 whenever the segment selection changes — page numbers for
  // the date-window view don't map onto the segment-scoped view.
  useEffect(() => { setPage(1); }, [activeSegment]);

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({
        date_from: filters.date_from, date_to: filters.date_to,
        sort_by: sortBy, page: String(page), page_size: '50',
      });
      if (filters.facility_id) qs.set('facility_id', filters.facility_id);
      if (filters.sport) qs.set('sport', filters.sport);
      // Segment filter: scope the backend roll-up to these phones (lifetime).
      if (segmentPhones !== null) {
        qs.set('phones', segmentPhones.join(','));
      }
      const res = await api.request(`/reports/customers?${qs.toString()}`);
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load customers');
    } finally { setLoading(false); }
  }, [filters, sortBy, page, segmentPhones]);

  const fetchSegments = useCallback(async () => {
    setSegmentsLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.facility_id) qs.set('facility_id', filters.facility_id);
      const path = qs.toString() ? `/reports/customer-segments?${qs.toString()}` : '/reports/customer-segments';
      const res = await api.request(path);
      setSegments(res);
    } catch (e) {
      // soft-fail — segments panel is auxiliary
      console.error('segments load failed', e);
    } finally { setSegmentsLoading(false); }
  }, [filters.facility_id]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { fetchSegments(); }, [fetchSegments]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.customers;
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(search));
    if (clientFilter === 'new') rows = rows.filter((c) => c.is_new_customer);
    if (clientFilter === 'churn') rows = rows.filter((c) => c.churn_risk);
    if (clientFilter === 'outstanding') rows = rows.filter((c) => c.outstanding_balance > 0);
    // Note: segment filtering is now done server-side via the `phones=` param
    // on /reports/customers, so we no longer intersect the paginated page
    // against `segment_phones` here — doing so would double-filter and
    // re-introduce the count mismatch this whole change is fixing.
    return rows;
  }, [data, search, clientFilter]);

  const exportCsv = () => {
    const qs = new URLSearchParams({
      date_from: filters.date_from, date_to: filters.date_to, sort_by: sortBy,
    });
    if (filters.facility_id) qs.set('facility_id', filters.facility_id);
    if (filters.sport) qs.set('sport', filters.sport);
    // Mirror the list: when a segment is active, export the segment-scoped
    // (lifetime) customers rather than the date-window view. What the admin
    // sees on screen is what they get in the CSV.
    let suggestedName = `PlaySpaces_Customers_${filters.date_from}_to_${filters.date_to}.csv`;
    if (segmentPhones !== null) {
      qs.set('phones', segmentPhones.join(','));
      suggestedName = `PlaySpaces_Customers_${activeSegment}.csv`;
    }
    downloadCsv(`/reports/export/customers-csv?${qs.toString()}`, suggestedName);
  };

  // Helper: which segments does this customer belong to (for badges)?
  const phoneSegments = useCallback((phone) => {
    if (!segments) return [];
    const out = [];
    for (const k of ['high_value', 'at_risk', 'loyal', 'new', 'monthly_debtors']) {
      if ((segments.segment_phones[k] || []).includes(phone)) out.push(k);
    }
    return out;
  }, [segments]);

  return (
    <div className="space-y-3">
      <SegmentsPanel
        segments={segments}
        loading={segmentsLoading}
        activeSegment={activeSegment}
        onSelect={setActiveSegment}
      />
      {activeSegment && (
        <div className="text-xs text-gray-500 flex items-center gap-2" data-testid="segment-filter-info">
          Filtering by <span className="font-semibold capitalize">{SEGMENT_META[activeSegment].label}</span>
          <button onClick={() => setActiveSegment(null)} className="text-blue-600 hover:underline" data-testid="clear-segment-filter">Clear</button>
        </div>
      )}

      {loading && !data ? <SkeletonList /> : error ? <ErrorBox msg={error} /> : !data ? null : (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-sm">Customers ({filtered.length}{filtered.length !== data.total_customers ? ` of ${data.total_customers}` : ''})</CardTitle>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="customers-export">
                <Download className="h-3.5 w-3.5 mr-1" />Export CSV
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input placeholder="Search name or phone" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-7 h-9 text-sm" data-testid="customers-search" />
              </div>
              <select className="h-9 border rounded px-2 text-sm bg-white" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} data-testid="customers-sort">
                <option value="total_spend">Sort: Total Spend</option>
                <option value="booking_count">Sort: Booking Count</option>
                <option value="last_booking">Sort: Last Booking</option>
                <option value="outstanding_balance">Sort: Outstanding</option>
              </select>
              <select className="h-9 border rounded px-2 text-sm bg-white" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="customers-filter">
                <option value="all">All Customers</option>
                <option value="new">New Customers</option>
                <option value="churn">Churn Risk</option>
                <option value="outstanding">Has Outstanding</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">No customers match.</div>
            ) : (
              <div className="space-y-2">
                {filtered.map((c) => {
                  const segKeys = phoneSegments(c.phone);
                  return (
                    <button
                      key={c.phone}
                      type="button"
                      onClick={() => setDrawerPhone(c.phone)}
                      className="w-full text-left border rounded-lg p-3 flex items-start gap-3 hover:bg-gray-50 hover:border-blue-300 transition focus:outline-none focus:ring-2 focus:ring-blue-300"
                      data-testid={`customer-row-${c.phone}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className="font-semibold text-sm truncate">{c.name || 'Unknown'}</span>
                          <span className="text-xs text-gray-500">·</span>
                          <span className="text-xs text-gray-600">{c.phone}</span>
                          {segKeys.includes('high_value') && <Badge cls="bg-blue-100 text-blue-800">HIGH VALUE</Badge>}
                          {segKeys.includes('at_risk') && <Badge cls="bg-red-100 text-red-700">AT RISK</Badge>}
                          {segKeys.includes('loyal') && <Badge cls="bg-purple-100 text-purple-700">LOYAL</Badge>}
                          {segKeys.includes('new') && <Badge cls="bg-green-100 text-green-700">NEW</Badge>}
                          {segKeys.includes('monthly_debtors') && <Badge cls="bg-amber-100 text-amber-700">MONTHLY DEBT</Badge>}
                          {c.outstanding_balance > 0 && !segKeys.includes('monthly_debtors') && <Badge cls="bg-amber-100 text-amber-700">DUE {fmtINR(c.outstanding_balance)}</Badge>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {c.booking_count} bookings · {fmtINR(c.total_spend)} spent · last booked {c.last_booking_date || '—'}
                          {c.days_since_last_booking != null && ` (${c.days_since_last_booking}d ago)`}
                        </div>
                        {c.sports_played?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.sports_played.map((s) => <Badge key={s} cls="bg-gray-100 text-gray-700 capitalize">{s}</Badge>)}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-3 text-sm">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="customers-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-gray-600">Page {page} of {data.total_pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={page >= data.total_pages} data-testid="customers-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CustomerProfileDrawer
        open={!!drawerPhone}
        phone={drawerPhone}
        onOpenChange={(v) => { if (!v) setDrawerPhone(null); }}
        facilityId={filters.facility_id}
        segmentKeys={drawerPhone ? phoneSegments(drawerPhone) : []}
      />
    </div>
  );
};

const Badge = ({ cls = '', children }) => (
  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>{children}</span>
);

const SkeletonList = () => (
  <div className="space-y-2 animate-pulse">
    {[1,2,3,4,5].map((i) => <div key={i} className="h-16 bg-gray-100 rounded" />)}
  </div>
);

// ---------- Pending payments tab ----------
// ---------- Shared report helpers ----------
const fmtHrs = (h) => {
  const v = Number(h || 0);
  return `${v.toFixed(v % 1 === 0 ? 0 : 1)}h`;
};
const occClass = (pct) => (pct >= 70 ? 'text-green-700' : pct >= 40 ? 'text-amber-600' : 'text-red-600');
const occBarBg = (pct) => (pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500');
const agingColor = (i) => ['bg-green-50 text-green-700', 'bg-lime-50 text-lime-700', 'bg-amber-50 text-amber-700', 'bg-red-50 text-red-700'][i] || 'bg-gray-50';

const downloadXlsx = (path, filename) => downloadCsv(path, filename);

const qsFromFilters = (filters, extra = {}) => {
  const qs = new URLSearchParams({ date_from: filters.date_from, date_to: filters.date_to, ...extra });
  if (filters.facility_id) qs.set('facility_id', filters.facility_id);
  return qs;
};

// ---------- Outstanding & Collections tab ----------
const OutstandingTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('customers'); // 'customers' | 'lineitems'
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = qsFromFilters(filters);
      setData(await api.request(`/reports/outstanding?${qs.toString()}`));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = () => downloadXlsx(`/reports/outstanding/export?${qsFromFilters(filters).toString()}`,
    `Outstanding_${filters.date_from}_to_${filters.date_to}.xlsx`);

  const sendLink = async (li) => {
    setBusyId(li.booking_id);
    try {
      const r = await api.request('/payments/create-payment-link', {
        method: 'POST',
        body: JSON.stringify({ booking_id: li.booking_id, amount: li.due, customer_name: li.customer_name, customer_phone: li.phone, description: `Outstanding for ${li.date}` }),
      });
      toast({ title: 'Payment link sent', description: r.short_url || 'Link created' });
    } catch (e) { toast({ title: 'Failed to send link', description: e.message, variant: 'destructive' }); }
    finally { setBusyId(null); }
  };
  const collectOffline = async (li) => {
    if (!window.confirm(`Mark ${fmtINR(li.due)} collected offline (cash) for ${li.customer_name}?`)) return;
    setBusyId(li.booking_id);
    try {
      await api.request(`/bookings/${li.booking_id}/collect-payment`, {
        method: 'POST', body: JSON.stringify({ amount: li.due, payment_method: 'cash_offline' }),
      });
      toast({ title: 'Marked collected', description: `${fmtINR(li.due)} recorded as cash` });
      load();
    } catch (e) { toast({ title: 'Failed to collect', description: e.message, variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  if (loading && !data) return <SkeletonList />;
  if (error) return <ErrorBox msg={error} />;
  if (!data) return null;

  const t = data.totals;
  const oldest60 = data.aging.find((a) => a.bucket === '60+') || { amount: 0, count: 0 };
  const empty = t.outstanding_count === 0;

  const customers = data.by_customer.filter((c) =>
    !search || (c.customer_name || '').toLowerCase().includes(search.toLowerCase()) || (c.phone || '').includes(search));
  const lineItems = data.line_items.filter((li) =>
    !search || (li.customer_name || '').toLowerCase().includes(search.toLowerCase()) || (li.phone || '').includes(search));

  return (
    <div className="space-y-3" data-testid="outstanding-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={IndianRupee} label="Total Outstanding" value={fmtINR(t.total_outstanding)} accent="text-red-600" testId="outstanding-total" />
        <Kpi icon={Users} label="Customers Owing" value={data.by_customer.length} testId="outstanding-customers" />
        <Kpi icon={AlertTriangle} label="Most Overdue (60+ days)" value={fmtINR(oldest60.amount)} sub={`${oldest60.count} items`} accent="text-red-600" testId="outstanding-overdue" />
        <Kpi icon={CreditCard} label="Booking vs Monthly" value={`${fmtINR(t.booking_dues)} · ${fmtINR(t.monthly_dues)}`} sub={`${t.booking_count} bookings · ${t.monthly_count} monthly`} testId="outstanding-split" />
      </div>

      {/* Aging bar */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Receivables Aging (as of {data.as_of})</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2" data-testid="outstanding-aging">
            {data.aging.map((a, i) => (
              <div key={a.bucket} className={`rounded-lg p-2 text-center ${agingColor(i)}`}>
                <div className="text-[11px] font-medium">{a.bucket} days</div>
                <div className="text-base font-bold">{fmtINR(a.amount)}</div>
                <div className="text-[11px]">{a.count} items</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm">Outstanding Detail</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <Button size="sm" variant={view === 'customers' ? 'default' : 'outline'} className="h-7 text-[11px] px-2" onClick={() => setView('customers')} data-testid="outstanding-view-customers">By Customer</Button>
              <Button size="sm" variant={view === 'lineitems' ? 'default' : 'outline'} className="h-7 text-[11px] px-2" onClick={() => setView('lineitems')} data-testid="outstanding-view-lineitems">Line Items</Button>
            </div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer" className="h-8 text-xs pl-7 w-40" data-testid="outstanding-search" />
            </div>
            <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="outstanding-export"><Download className="h-3.5 w-3.5 mr-1" />Export Excel</Button>
          </div>
        </CardHeader>
        <CardContent>
          {empty ? <Empty msg="No outstanding dues for this range." /> : view === 'customers' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="outstanding-customer-table">
                <thead><tr className="text-left text-gray-500 text-xs border-b">
                  <th className="py-2 pr-2">Customer</th><th className="pr-2">Phone</th><th className="pr-2 text-right">Total Due</th>
                  <th className="pr-2 text-right">Oldest (days)</th><th className="pr-2 text-right">Booking</th><th className="pr-2 text-right">Monthly</th>
                </tr></thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.phone} className={`border-b ${c.oldest_due_days > 60 ? 'bg-red-50' : c.oldest_due_days > 30 ? 'bg-amber-50' : ''}`} data-testid="outstanding-customer-row">
                      <td className="py-2 pr-2 font-medium">{c.customer_name}</td>
                      <td className="pr-2 text-gray-500">{c.phone}</td>
                      <td className="pr-2 text-right font-semibold text-red-600">{fmtINR(c.total_outstanding)}</td>
                      <td className={`pr-2 text-right font-medium ${c.oldest_due_days > 30 ? 'text-red-600' : ''}`}>{c.oldest_due_days}</td>
                      <td className="pr-2 text-right text-gray-600">{fmtINR(c.booking_dues)}</td>
                      <td className="pr-2 text-right text-gray-600">{fmtINR(c.monthly_dues)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="outstanding-lineitem-table">
                <thead><tr className="text-left text-gray-500 text-xs border-b">
                  <th className="py-2 pr-2">Date</th><th className="pr-2">Customer</th><th className="pr-2">Court</th>
                  <th className="pr-2 text-right">Due</th><th className="pr-2 text-right">Age</th><th className="pr-2">Type</th><th className="pr-2 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {lineItems.map((li) => (
                    <tr key={li.booking_id} className="border-b" data-testid="outstanding-lineitem-row">
                      <td className="py-2 pr-2 whitespace-nowrap">{li.date}<span className="text-gray-400"> {li.start_time}</span></td>
                      <td className="pr-2"><div className="font-medium">{li.customer_name}</div><div className="text-[11px] text-gray-400">{li.phone}</div></td>
                      <td className="pr-2 text-gray-600">{li.court}</td>
                      <td className="pr-2 text-right font-semibold text-red-600">{fmtINR(li.due)}</td>
                      <td className={`pr-2 text-right ${li.age_days > 30 ? 'text-red-600 font-medium' : ''}`}>{li.age_days}d</td>
                      <td className="pr-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${li.type === 'monthly' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{li.type}</span></td>
                      <td className="pr-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 mr-1" disabled={busyId === li.booking_id} onClick={() => sendLink(li)} data-testid={`outstanding-sendlink-${li.booking_id}`}>Send Link</Button>
                        <Button size="sm" className="h-7 text-[11px] px-2 bg-green-600 hover:bg-green-700" disabled={busyId === li.booking_id} onClick={() => collectOffline(li)} data-testid={`outstanding-collect-${li.booking_id}`}>Collect</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- Court Utilisation tab ----------
const UtilisationTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setData(await api.request(`/reports/utilisation?${qsFromFilters(filters).toString()}`, { timeout: 60000 }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = () => downloadXlsx(`/reports/utilisation/export?${qsFromFilters(filters).toString()}`,
    `Utilisation_${filters.date_from}_to_${filters.date_to}.xlsx`);

  if (loading && !data) return <SkeletonList />;
  if (error) return <ErrorBox msg={error} />;
  if (!data) return null;

  const h = data.headline;
  const empty = (data.per_court || []).length === 0 || h.total_available_hours === 0;
  const peakLabel = (h.peak_hours || []).map((x) => `${String(x).padStart(2, '0')}:00`).join(', ') || '—';

  return (
    <div className="space-y-3" data-testid="utilisation-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="utilisation-export"><Download className="h-3.5 w-3.5 mr-1" />Export Excel</Button>
      </div>
      {h.default_hours_used && (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1" data-testid="utilisation-default-hours">Default operating hours (06:00–23:00) assumed for some venues without configured hours.</div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={TrendingUp} label="Overall Occupancy" value={fmtPct(h.overall_occupancy_rate)} accent={occClass(h.overall_occupancy_rate)} testId="util-overall" />
        <Kpi icon={TrendingUp} label="Peak Occupancy" value={fmtPct(h.peak_occupancy_rate)} accent={occClass(h.peak_occupancy_rate)} testId="util-peak" />
        <Kpi icon={TrendingDown} label="Off-Peak Occupancy" value={fmtPct(h.off_peak_occupancy_rate)} accent={occClass(h.off_peak_occupancy_rate)} testId="util-offpeak" />
        <Kpi icon={Clock} label="Booked Hours" value={fmtHrs(h.total_booked_hours)} sub={`of ${fmtHrs(h.total_available_hours)} avail`} testId="util-booked" />
        <Kpi icon={IndianRupee} label="Rev / Avail Hr" value={fmtINR(h.revenue_per_available_hour)} testId="util-revperhr" />
        <Kpi icon={BarChart3} label="Best / Worst Court" value={h.best_court ? `${fmtPct(h.best_court.occupancy_rate)}` : '—'} sub={h.best_court ? `${h.best_court.court_name}` : ''} testId="util-bestworst" />
      </div>
      <div className="text-[11px] text-gray-500">Peak hours (derived from demand): <span className="font-medium text-gray-700">{peakLabel}</span></div>

      {empty ? <Empty msg="No bookings for this range." /> : (
        <>
          {(data.turf_occupancy || []).length > 0 && (
            <Card className="border-l-4 border-l-blue-500" data-testid="util-turf-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Physical Turf Occupancy {h.physical_turf_occupancy_rate != null && (<span className="ml-1 text-blue-600 font-bold">{fmtPct(h.physical_turf_occupancy_rate)}</span>)}</CardTitle>
                <p className="text-[11px] text-gray-500">Real utilisation of each physical ground — a booking on any sub-format counts against the whole shared pitch (overlap-weighted, capped at 100%/slot).</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2" data-testid="util-turf-list">
                  {data.turf_occupancy.map((g) => (
                    <div key={`${g.facility_id}-${g.ground}`} className="flex items-center gap-3" data-testid="util-turf-row">
                      <div className="w-48 truncate text-sm font-medium">{g.ground_label}{g.is_divisible && <span className="ml-1 text-[10px] text-blue-500 uppercase">shared</span>}</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-2.5"><div className={`${occBarBg(g.occupancy_rate)} h-2.5 rounded-full`} style={{ width: `${Math.min(100, g.occupancy_rate)}%` }} /></div>
                      <span className={`text-xs font-semibold w-12 text-right ${occClass(g.occupancy_rate)}`}>{fmtPct(g.occupancy_rate)}</span>
                      <span className="text-[10px] text-gray-400 w-24 text-right">{fmtHrs(g.occupied_hours)}/{fmtHrs(g.available_hours)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Occupancy by Court <span className="font-normal text-gray-400 text-xs">· physical utilisation</span></CardTitle>
              <p className="text-[11px] text-gray-500">Each court's occupancy includes every overlapping format physically sitting on its ground (e.g. an 11v11 fills from its 9v9 / 7v7 bookings).</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="util-court-table">
                  <thead><tr className="text-left text-gray-500 text-xs border-b">
                    <th className="py-2 pr-2">Court</th><th className="pr-2">Format</th><th className="pr-2 w-40">Occupancy</th>
                    <th className="pr-2 text-right">Booked</th><th className="pr-2 text-right">Avail</th><th className="pr-2 text-right">Bookings</th>
                    <th className="pr-2 text-right">Revenue</th><th className="pr-2 text-right">Rev/Avail</th>
                  </tr></thead>
                  <tbody>
                    {data.per_court.map((c) => (
                      <tr key={c.court_id} className="border-b" data-testid="util-court-row">
                        <td className="py-2 pr-2 font-medium">{c.court_name}</td>
                        <td className="pr-2 text-gray-500 capitalize">{c.format}</td>
                        <td className="pr-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-2.5"><div className={`${occBarBg(c.occupancy_rate)} h-2.5 rounded-full`} style={{ width: `${Math.min(100, c.occupancy_rate)}%` }} /></div>
                            <span className={`text-xs font-semibold w-12 text-right ${occClass(c.occupancy_rate)}`}>{fmtPct(c.occupancy_rate)}</span>
                          </div>
                        </td>
                        <td className="pr-2 text-right">{fmtHrs(c.booked_hours)}</td>
                        <td className="pr-2 text-right text-gray-400">{fmtHrs(c.available_hours)}</td>
                        <td className="pr-2 text-right">{c.bookings_count}</td>
                        <td className="pr-2 text-right">{fmtINR(c.revenue)}</td>
                        <td className="pr-2 text-right text-gray-600">{fmtINR(c.revenue_per_available_hour)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Demand by Format</CardTitle>
                <p className="text-[11px] text-gray-500">Which formats customers actually book (booked hours, not physical occupancy).</p>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm" data-testid="util-format-table">
                  <thead><tr className="text-left text-gray-500 text-xs border-b"><th className="py-2 pr-2">Format</th><th className="pr-2 text-right">Booked</th><th className="pr-2 text-right">Revenue</th><th className="pr-2 text-right">Capacity Share</th></tr></thead>
                  <tbody>
                    {data.by_format.map((f) => (
                      <tr key={f.format} className="border-b"><td className="py-2 pr-2 capitalize">{f.format}</td><td className="pr-2 text-right">{fmtHrs(f.booked_hours)}</td><td className="pr-2 text-right">{fmtINR(f.revenue)}</td><td className="pr-2 text-right">{fmtPct(f.capacity_share_pct)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Occupancy Trend</CardTitle></CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={data.occupancy_trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} unit="%" />
                      <Tooltip formatter={(v) => `${v}%`} />
                      <Line type="monotone" dataKey="occupancy_rate" stroke="#2563eb" strokeWidth={2} dot={false} name="Occupancy" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            {(data.turf_trend || []).length > 0 && (() => {
              const series = data.turf_trend;
              const dates = (series[0]?.points || []).map(p => p.date);
              const rows = dates.map((ds, i) => {
                const r = { date: ds };
                series.forEach(s => { r[s.ground_label] = s.points[i]?.occupancy_rate ?? 0; });
                return r;
              });
              const palette = ['#2563eb', '#16a34a', '#f59e0b', '#db2777', '#7c3aed', '#0891b2', '#dc2626'];
              return (
                <Card data-testid="util-turf-trend">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Physical Turf Occupancy Trend</CardTitle>
                    <p className="text-[11px] text-gray-500">Daily physical utilisation per ground — spot the busiest days.</p>
                  </CardHeader>
                  <CardContent>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <LineChart data={rows} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                          <YAxis tick={{ fontSize: 10 }} unit="%" />
                          <Tooltip formatter={(v) => `${v}%`} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          {series.map((s, i) => (
                            <Line key={s.ground_label} type="monotone" dataKey={s.ground_label} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Demand Patterns tab ----------
const heatColor = (pct, metricMax, metric, value) => {
  // intensity 0..1 — occupancy uses % directly, others scale to max
  let intensity = 0;
  if (metric === 'occupancy_rate') intensity = Math.min(1, (pct || 0) / 100);
  else intensity = metricMax > 0 ? Math.min(1, (value || 0) / metricMax) : 0;
  if (intensity === 0) return '#f8fafc';
  // light blue → deep red scale
  const r = Math.round(239 - intensity * (239 - 185));
  const g = Math.round(246 - intensity * 200);
  const b = Math.round(255 - intensity * 200);
  return `rgb(${r},${g},${b})`;
};

const DemandHeatmap = ({ heatmap, hours, metric }) => {
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const lookup = {};
  heatmap.forEach((c) => { lookup[`${c.dow}-${c.hour}`] = c; });
  const metricMax = Math.max(...heatmap.map((c) => c[metric] || 0), 1);
  const fmtCell = (c) => {
    if (!c) return '';
    if (metric === 'occupancy_rate') return c.occupancy_rate ? `${Math.round(c.occupancy_rate)}` : '';
    if (metric === 'revenue') return c.revenue ? Math.round(c.revenue) : '';
    return c.bookings || '';
  };
  return (
    <div className="overflow-x-auto" data-testid="demand-heatmap">
      <table className="border-collapse" style={{ fontSize: 10 }}>
        <thead>
          <tr>
            <th className="sticky left-0 bg-white p-1"></th>
            {hours.map((h) => <th key={h} className="p-1 text-gray-500 font-normal">{String(h).padStart(2, '0')}</th>)}
          </tr>
        </thead>
        <tbody>
          {DAYS.map((d, dow) => (
            <tr key={dow}>
              <td className="sticky left-0 bg-white pr-2 font-medium text-gray-600">{d}</td>
              {hours.map((h) => {
                const c = lookup[`${dow}-${h}`];
                return (
                  <td key={h} className="text-center" style={{
                    background: heatColor(c?.occupancy_rate, metricMax, metric, c?.[metric]),
                    width: 26, height: 24, color: (c?.occupancy_rate || 0) > 55 ? '#fff' : '#475569',
                  }} title={c ? `${d} ${String(h).padStart(2,'0')}:00 · ${Math.round(c.occupancy_rate)}% · ${c.bookings} bookings · ${fmtINR(c.revenue)}` : ''}>
                    {fmtCell(c)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DemandPatternsTab = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [metric, setMetric] = useState('occupancy_rate');
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setData(await api.request(`/reports/demand-patterns?${qsFromFilters(filters).toString()}`, { timeout: 60000 }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = () => downloadXlsx(`/reports/demand-patterns/export?${qsFromFilters(filters).toString()}`,
    `DemandPatterns_${filters.date_from}_to_${filters.date_to}.xlsx`);

  if (loading && !data) return <SkeletonList />;
  if (error) return <ErrorBox msg={error} />;
  if (!data) return null;

  const empty = !data.heatmap || data.heatmap.every((c) => c.bookings === 0);

  return (
    <div className="space-y-3" data-testid="demand-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {[['occupancy_rate', 'Occupancy %'], ['bookings', 'Bookings'], ['revenue', 'Revenue']].map(([k, l]) => (
            <Button key={k} size="sm" variant={metric === k ? 'default' : 'outline'} className="h-7 text-[11px] px-2" onClick={() => setMetric(k)} data-testid={`demand-metric-${k}`}>{l}</Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="demand-export"><Download className="h-3.5 w-3.5 mr-1" />Export Excel</Button>
      </div>

      {empty ? <Empty msg="No bookings for this range." /> : (
        <>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Demand Heatmap — Day × Hour</CardTitle><p className="text-[10px] text-gray-400">Darker = busier. Showing {metric === 'occupancy_rate' ? 'occupancy %' : metric}.</p></CardHeader>
            <CardContent><DemandHeatmap heatmap={data.heatmap} hours={data.operating_hours} metric={metric} /></CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card data-testid="demand-priceup">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-red-700">Price-Increase Candidates (always full ≥90%)</CardTitle></CardHeader>
              <CardContent>
                {data.always_full_slots.length === 0 ? <Empty msg="No consistently-full slots yet." /> : (
                  <div className="space-y-1.5" data-testid="demand-priceup-list">
                    {data.always_full_slots.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                        <span className="font-medium">{DAYS[s.dow]} {String(s.hour).padStart(2, '0')}:00</span>
                        <span className="text-red-700 font-semibold">{fmtPct(s.occupancy_rate)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-green-700">Run-an-Offer Candidates (dead slots)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5" data-testid="demand-deadslots">
                  {data.dead_slots.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                      <span className="font-medium">{DAYS[s.dow]} {String(s.hour).padStart(2, '0')}:00</span>
                      <span className="text-green-700 font-semibold">{fmtPct(s.occupancy_rate)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi icon={TrendingUp} label="Busiest Day" value={data.busiest_day != null ? DAYS[data.busiest_day] : '—'} testId="demand-busiest-day" />
            <Kpi icon={Clock} label="Busiest Hour" value={data.busiest_hour != null ? `${String(data.busiest_hour).padStart(2, '0')}:00` : '—'} testId="demand-busiest-hour" />
            <Kpi icon={Clock} label="Avg Lead Time" value={`${data.lead_time.avg_days} days`} testId="demand-leadtime" />
            <Kpi icon={CreditCard} label="Peak Hours" value={(data.peak_hours || []).length} sub={(data.peak_hours || []).map((x) => String(x).padStart(2, '0')).join(', ')} testId="demand-peakcount" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Booking Lead Time</CardTitle></CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.lead_time.distribution} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} name="Bookings" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Source Split — Peak Hours</CardTitle></CardHeader>
              <CardContent>
                {data.source_by_time.length === 0 ? <Empty msg="No peak-hour bookings." /> : (
                  <table className="w-full text-sm" data-testid="demand-source-table">
                    <thead><tr className="text-left text-gray-500 text-xs border-b"><th className="py-2 pr-2">Source</th><th className="pr-2 text-right">Bookings</th></tr></thead>
                    <tbody>
                      {data.source_by_time.map((s) => (
                        <tr key={s.source} className="border-b"><td className="py-2 pr-2 capitalize">{s.source}</td><td className="pr-2 text-right font-medium">{s.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Main page ----------
const Reports = () => {
  const { user, isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    date_from: searchParams.get('date_from') || daysAgo(29),
    date_to: searchParams.get('date_to') || today(),
    facility_id: searchParams.get('facility_id') || null,
    sport: searchParams.get('sport') || null,
  }));
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [facilities, setFacilities] = useState([]);

  // Load facilities for superadmin
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        setFacilities(await api.getFacilities() || []);
      } catch (err) {
        console.error('Failed to load facility list:', err);
        toast({
          title: 'Could not load facility list',
          description: err.message || 'Venue filter will be unavailable until you refresh.',
          variant: 'destructive',
        });
      }
    })();
  }, [isSuperAdmin]);

  // Sync URL
  useEffect(() => {
    const sp = new URLSearchParams();
    sp.set('date_from', appliedFilters.date_from);
    sp.set('date_to', appliedFilters.date_to);
    if (appliedFilters.facility_id) sp.set('facility_id', appliedFilters.facility_id);
    if (appliedFilters.sport) sp.set('sport', appliedFilters.sport);
    sp.set('tab', activeTab);
    setSearchParams(sp, { replace: true });
  }, [appliedFilters, activeTab, setSearchParams]);

  // Fetch summary on applied-filter change
  const fetchSummary = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
      });
      if (appliedFilters.facility_id) qs.set('facility_id', appliedFilters.facility_id);
      if (appliedFilters.sport) qs.set('sport', appliedFilters.sport);
      const res = await api.request(`/reports/summary?${qs.toString()}`);
      setSummary(res);
    } catch (e) {
      setError(e.message || 'Failed to load summary');
    } finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const apply = () => setAppliedFilters(filters);

  const exportSummaryCsv = () => {
    const qs = new URLSearchParams({
      date_from: appliedFilters.date_from, date_to: appliedFilters.date_to,
    });
    if (appliedFilters.facility_id) qs.set('facility_id', appliedFilters.facility_id);
    if (appliedFilters.sport) qs.set('sport', appliedFilters.sport);
    downloadCsv(`/reports/export/summary-csv?${qs.toString()}`,
      `PlaySpaces_Summary_${appliedFilters.date_from}_to_${appliedFilters.date_to}.csv`);
  };

  const exportVenueRevenueCsv = () => {
    const qs = new URLSearchParams({
      date_from: appliedFilters.date_from, date_to: appliedFilters.date_to,
    });
    if (appliedFilters.facility_id) qs.set('facility_id', appliedFilters.facility_id);
    if (appliedFilters.sport) qs.set('sport', appliedFilters.sport);
    downloadCsv(`/reports/export/venue-revenue-csv?${qs.toString()}`,
      `PlaySpaces_VenueRevenue_${appliedFilters.date_from}_to_${appliedFilters.date_to}.csv`);
  };

  const exportBookingsCsv = () => {
    const qs = new URLSearchParams({
      date_from: appliedFilters.date_from, date_to: appliedFilters.date_to,
    });
    if (appliedFilters.facility_id) qs.set('facility_id', appliedFilters.facility_id);
    if (appliedFilters.sport) qs.set('sport', appliedFilters.sport);
    downloadCsv(`/reports/export/bookings-csv?${qs.toString()}`,
      `PlaySpaces_Bookings_${appliedFilters.date_from}_to_${appliedFilters.date_to}.csv`);
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadVenuePdf = async () => {
    // Owner-facing report is per-venue. Superadmin must pick a venue first;
    // admin / venue_manager are auto-scoped server-side.
    if (isSuperAdmin && !appliedFilters.facility_id) {
      toast({
        title: 'Select a venue first',
        description: 'The Venue Performance Report is generated for a single venue. Pick a venue in the filter, then Apply.',
        variant: 'destructive',
      });
      return;
    }
    setPdfLoading(true);
    const qs = new URLSearchParams({
      date_from: appliedFilters.date_from, date_to: appliedFilters.date_to,
    });
    if (appliedFilters.facility_id) qs.set('facility_id', appliedFilters.facility_id);
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/venue-performance/pdf?${qs.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Server returned ${res.status}`;
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `VenuePerformance_${appliedFilters.date_from}_to_${appliedFilters.date_to}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast({ title: 'Venue report ready', description: 'Your PDF download has started.' });
    } catch (e) {
      toast({ title: 'Could not generate report', description: e.message, variant: 'destructive' });
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-4" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-gray-500 text-sm">Server-side aggregations · always accurate at any scale</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading} data-testid="reports-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportVenueRevenueCsv} data-testid="reports-export-venue-revenue">
            <Download className="h-4 w-4 mr-2" />Venue Revenue CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportBookingsCsv} data-testid="reports-export-bookings">
            <Download className="h-4 w-4 mr-2" />Bookings CSV
          </Button>
          <Button size="sm" onClick={exportSummaryCsv} data-testid="reports-export-summary">
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
          <Button
            size="sm"
            onClick={downloadVenuePdf}
            disabled={pdfLoading}
            className="bg-[#14b8a6] hover:bg-[#0f9488] text-white"
            data-testid="reports-download-venue-pdf"
          >
            {pdfLoading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <FileText className="h-4 w-4 mr-2" />}
            Venue PDF
          </Button>
        </div>
      </div>

      <FiltersBar
        filters={filters}
        onChange={setFilters}
        onApply={apply}
        loading={loading}
        isSuperadmin={isSuperAdmin}
        facilities={facilities}
        facilityName={user?.facility_name || (user?.facility_id ? facilities.find((f) => f.id === user.facility_id)?.name || user.facility_id : null)}
      />

      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'customers', label: 'Customers', icon: Users },
          { id: 'pending', label: 'Outstanding', icon: CreditCard },
          { id: 'usage', label: 'Utilisation', icon: Clock },
          { id: 'peak', label: 'Demand Patterns', icon: TrendingUp },
        ].map((t) => (
          <Button key={t.id} size="sm"
            variant={activeTab === t.id ? 'default' : 'outline'}
            onClick={() => setActiveTab(t.id)}
            className="whitespace-nowrap"
            data-testid={`reports-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4 mr-1.5" />{t.label}
          </Button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab summary={summary} loading={loading} error={error} />}
      {activeTab === 'customers' && <CustomersTab filters={appliedFilters} />}
      {activeTab === 'pending' && <OutstandingTab filters={appliedFilters} />}
      {activeTab === 'usage' && <UtilisationTab filters={appliedFilters} />}
      {activeTab === 'peak' && <DemandPatternsTab filters={appliedFilters} />}
    </div>
  );
};

export default Reports;
